import React, {useMemo} from "react";
import {Box, Tooltip} from "@mui/material";
import Typography from "../../../components/Typography";
import {entryColor, entryTypeLabel} from "./WorkDayRecordHistoryWidget";
import {BACKGROUND_IMAGE} from "../../../config";


const SCALE_START_HOUR = 6;
const SCALE_END_HOUR = 20;
const SCALE_MINUTES = (SCALE_END_HOUR - SCALE_START_HOUR) * 60;

function getCurrentTimeHHMM() {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
}

function timeToMinutes(time: string): number {
    const [h, m] = time.split(':').map(Number);
    return h * 60 + m;
}

function clamp(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, value));
}

function segmentGeometry(
    startTime: string,
    endTime: string | null,
): [number, number] | null {

    const scaleStart = SCALE_START_HOUR * 60;
    const scaleEnd = SCALE_END_HOUR * 60;

    const start = timeToMinutes(startTime);
    const end = endTime ? timeToMinutes(endTime) : scaleEnd;

    const clampedStart = clamp(start, scaleStart, scaleEnd);
    const clampedEnd = clamp(end, scaleStart, scaleEnd);

    if (clampedEnd <= clampedStart) {
        return null;
    }

    const left = ((clampedStart - scaleStart) / SCALE_MINUTES) * 100;
    const width = ((clampedEnd - clampedStart) / SCALE_MINUTES) * 100;

    return [left, width];
}

function formatTime(time: string): string {
    return time ? time.substring(0, 5) : '';
}

export interface TimeScaleEntry {
    startTime: string;
    endTime: string | null;
    entryType: string;
}

interface TimeScaleProps {
    entries: TimeScaleEntry[];
    isClockedIn: boolean;
    isCorrection: boolean;
}

export const TimeScale: React.FC<TimeScaleProps> = ({entries, isClockedIn, isCorrection}) => {

    const ticks = useMemo(() => {
        const t: number[] = [];
        for (let h = SCALE_START_HOUR; h <= SCALE_END_HOUR; h += 4) t.push(h);
        return t;
    }, []);

    return (
        <Box display="flex" flexDirection="row" gap={1}>
            <Box
                position="relative"
                width={25}
                height={300}
                sx={{backgroundColor: 'action.hover', borderRadius: 1, overflow: 'hidden'}}
            >
                {entries.map((entry) => {

                    const isOpen = !entry.endTime;
                    const endTime = entry.endTime ? entry.endTime : getCurrentTimeHHMM();
                    const geo = segmentGeometry(entry.startTime, endTime);

                    if (!geo) {
                        return null;
                    }

                    const [top, height] = geo; // same values, different meaning now

                    const tipLabel = `${entryTypeLabel(entry.entryType)}: ${formatTime(entry.startTime)} – 
                            ${isOpen ? 'jetzt' : formatTime(endTime)}`;

                    return (
                        <Tooltip key={`${entry.entryType}-${entry.startTime}-${entry.endTime}`}
                                 title={tipLabel} placement="right" arrow>

                            <Box position="absolute" left={0} right={0}
                                 top={`${top}%`} height={`${height}%`}
                                 sx={{
                                     backgroundColor: entryColor(entry.entryType),
                                     ...(isCorrection && {
                                         backgroundImage: BACKGROUND_IMAGE
                                     })
                                     // opacity: isOpen ? 0.85 : 1,
                                     // ...(isOpen && isClockedIn && {
                                     //     '&::after': {
                                     //         content: '""',
                                     //         position: 'absolute',
                                     //         bottom: 0,
                                     //         left: 0,
                                     //         right: 0,
                                     //         height: 3,
                                     //         backgroundColor: 'background.paper',
                                     //         opacity: 0.6,
                                     //     },
                                     // }),
                                 }}
                            />
                        </Tooltip>
                    );
                })}
            </Box>

            <Box position="relative" height={300} width={40}>
                {ticks.map((h) => {
                    const pct =
                        ((h - SCALE_START_HOUR) /
                            (SCALE_END_HOUR - SCALE_START_HOUR)) *
                        100;

                    return (
                        <Typography
                            key={h}
                            variant="caption"
                            color="secondary"
                            className="absolute -translate-y-1/2 text-[0.6rem] leading-none whitespace-nowrap"
                            style={{top: `${pct}%`}}
                        >
                            {String(h).padStart(2, '0')}:00
                        </Typography>
                    );
                })}
            </Box>
        </Box>
    );
};